import { useState } from 'react';
import { ShieldExclamationIcon, NoSymbolIcon, SpeakerXMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ClubTheme } from './themes';
import type { SocialMember, SocialAuthor } from './types';
import { useClubMembers } from './queries';
import { useAuth } from '@/lib/auth-context';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

interface ModerationPanelProps {
  clubId: string;
  theme: ClubTheme;
}

interface ModLogEntry {
  id: string;
  action: string;
  target_type: string;
  target_id: string;
  reason: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  actor: SocialAuthor | null;
}

type ModAction = 'ban' | 'mute' | 'warn';

const DURATION_OPTIONS = [1, 6, 12, 24, 48, 72, 168];

function formatDuration(hours: number): string {
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return days === 7 ? '1 week' : `${days}d`;
}

export default function ModerationPanel({ clubId, theme }: ModerationPanelProps) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  const [tab, setTab] = useState<'actions' | 'log'>('actions');
  const [selectedMember, setSelectedMember] = useState<SocialMember | null>(null);
  const [action, setAction] = useState<ModAction>('warn');
  const [reason, setReason] = useState('');
  const [muteDuration, setMuteDuration] = useState(24);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Log state
  const [logEntries, setLogEntries] = useState<ModLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);

  const { data: members = [] } = useClubMembers(clubId);

  const loadLog = async () => {
    setLogLoading(true);
    try {
      const res = await fetch(`${CORE_API}/api/v1/social/${clubId}/mod/log?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setLogEntries(json.data?.entries || []);
    } catch {
      setLogEntries([]);
    }
    setLogLoading(false);
  };

  const handleTabChange = (t: 'actions' | 'log') => {
    setTab(t);
    if (t === 'log') loadLog();
  };

  const handleSubmit = async () => {
    if (!selectedMember?.user?.id) return;
    if (action === 'warn' && !reason.trim()) {
      setFeedback({ type: 'error', text: 'Reason is required for warnings.' });
      return;
    }

    setSubmitting(true);
    setFeedback(null);

    try {
      const payload: Record<string, unknown> = {
        user_id: selectedMember.user.id,
        reason: reason.trim(),
      };
      if (action === 'mute') {
        payload.duration_hours = muteDuration;
      }

      const res = await fetch(`${CORE_API}/api/v1/social/${clubId}/mod/${action}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (!res.ok) {
        setFeedback({ type: 'error', text: json.error || 'Action failed.' });
      } else {
        const name = selectedMember.user?.full_name || 'User';
        const verb = action === 'ban' ? 'banned' : action === 'mute' ? 'muted' : 'warned';
        setFeedback({ type: 'success', text: `${name} has been ${verb}.` });
        setSelectedMember(null);
        setReason('');
      }
    } catch {
      setFeedback({ type: 'error', text: 'Network error.' });
    }

    setSubmitting(false);
  };

  const actionConfig: Record<ModAction, { icon: typeof NoSymbolIcon; label: string; color: string }> = {
    ban: { icon: NoSymbolIcon, label: 'Ban', color: '#EF4444' },
    mute: { icon: SpeakerXMarkIcon, label: 'Mute', color: '#F59E0B' },
    warn: { icon: ExclamationTriangleIcon, label: 'Warn', color: '#3B82F6' },
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <ShieldExclamationIcon className="h-5 w-5" style={{ color: theme.colors.primary }} />
        <h2 className="text-sm font-bold" style={{ color: theme.colors.text }}>
          Moderation
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {(['actions', 'log'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => handleTabChange(t)}
            className="rounded-lg px-3 py-1 text-xs font-semibold capitalize transition-colors"
            style={{
              backgroundColor: tab === t ? theme.colors.primary : `${theme.colors.muted}15`,
              color: tab === t ? theme.colors.bg : theme.colors.muted,
            }}
          >
            {t === 'log' ? 'Audit Log' : 'Actions'}
          </button>
        ))}
      </div>

      {/* Actions tab */}
      {tab === 'actions' && (
        <div className="space-y-3">
          {/* Member select */}
          <div>
            <label className="mb-1 block text-[11px] font-medium" style={{ color: theme.colors.muted }}>
              Select Member
            </label>
            <select
              value={selectedMember?.user?.id || ''}
              onChange={(e) => {
                const m = members.find((m) => m.user?.id === e.target.value);
                setSelectedMember(m || null);
              }}
              className="w-full rounded-lg px-3 py-2 text-sm outline-none"
              style={{
                backgroundColor: `${theme.colors.muted}15`,
                color: theme.colors.text,
                border: `1px solid ${theme.colors.muted}30`,
              }}
            >
              <option value="">Choose a member...</option>
              {members.map((m) => (
                <option key={m.id} value={m.user?.id || ''}>
                  {m.user?.full_name || 'Unknown'} ({m.club_role})
                </option>
              ))}
            </select>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            {(Object.entries(actionConfig) as [ModAction, typeof actionConfig.ban][]).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setAction(key)}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
                  style={{
                    backgroundColor: action === key ? `${cfg.color}20` : `${theme.colors.muted}10`,
                    color: action === key ? cfg.color : theme.colors.muted,
                    border: action === key ? `1px solid ${cfg.color}40` : '1px solid transparent',
                  }}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {cfg.label}
                </button>
              );
            })}
          </div>

          {/* Mute duration */}
          {action === 'mute' && (
            <div>
              <label className="mb-1 block text-[11px] font-medium" style={{ color: theme.colors.muted }}>
                Duration
              </label>
              <div className="flex flex-wrap gap-1.5">
                {DURATION_OPTIONS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setMuteDuration(h)}
                    className="rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors"
                    style={{
                      backgroundColor: muteDuration === h ? theme.colors.primary : `${theme.colors.muted}15`,
                      color: muteDuration === h ? theme.colors.bg : theme.colors.muted,
                    }}
                  >
                    {formatDuration(h)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="mb-1 block text-[11px] font-medium" style={{ color: theme.colors.muted }}>
              Reason {action === 'warn' && <span style={{ color: '#EF4444' }}>*</span>}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Describe the reason for this action..."
              className="w-full resize-none rounded-lg px-3 py-2 text-sm outline-none"
              style={{
                backgroundColor: `${theme.colors.muted}15`,
                color: theme.colors.text,
                border: `1px solid ${theme.colors.muted}30`,
              }}
            />
          </div>

          {/* Feedback */}
          {feedback && (
            <div
              className="rounded-lg px-3 py-2 text-xs font-medium"
              style={{
                backgroundColor: feedback.type === 'success' ? '#10B98120' : '#EF444420',
                color: feedback.type === 'success' ? '#10B981' : '#EF4444',
              }}
            >
              {feedback.text}
            </div>
          )}

          {/* Submit */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedMember || submitting}
            className="w-full rounded-lg py-2 text-sm font-semibold transition-opacity disabled:opacity-40"
            style={{
              backgroundColor: actionConfig[action].color,
              color: '#FFFFFF',
            }}
          >
            {submitting ? 'Submitting...' : `${actionConfig[action].label} Member`}
          </button>
        </div>
      )}

      {/* Audit Log tab */}
      {tab === 'log' && (
        <div className="space-y-2">
          {logLoading && (
            <div className="py-6 text-center">
              <div
                className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-t-transparent"
                style={{ borderColor: `${theme.colors.muted}40`, borderTopColor: theme.colors.primary }}
              />
            </div>
          )}

          {!logLoading && logEntries.length === 0 && (
            <p className="py-4 text-center text-xs" style={{ color: theme.colors.muted }}>
              No moderation actions recorded.
            </p>
          )}

          {logEntries.map((entry) => (
            <div
              key={entry.id}
              className="rounded-lg p-3"
              style={{ backgroundColor: `${theme.colors.muted}08`, border: `1px solid ${theme.colors.muted}15` }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                    style={{
                      backgroundColor:
                        entry.action === 'ban' ? '#EF444420'
                        : entry.action === 'mute' ? '#F59E0B20'
                        : entry.action === 'warn' ? '#3B82F620'
                        : `${theme.colors.muted}20`,
                      color:
                        entry.action === 'ban' ? '#EF4444'
                        : entry.action === 'mute' ? '#F59E0B'
                        : entry.action === 'warn' ? '#3B82F6'
                        : theme.colors.muted,
                    }}
                  >
                    {entry.action}
                  </span>
                  <span className="text-xs font-medium" style={{ color: theme.colors.text }}>
                    {entry.actor?.full_name || 'System'}
                  </span>
                </div>
                <span className="text-[10px]" style={{ color: theme.colors.muted }}>
                  {new Date(entry.created_at).toLocaleString()}
                </span>
              </div>
              {entry.reason && (
                <p className="mt-1 text-xs" style={{ color: theme.colors.muted }}>
                  {entry.reason}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
